import { useEffect, useState } from "react";

const initialProjects = [
  {
    title: "OneUBank MFB Personal",
    description:
      "Personal banking app for account access, transfers, payments, and everyday money management.",
    url: "https://play.google.com/store/apps/details?id=com.one_u_bank.personal",
    featureImage: "",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
    accent: "#fef3c7",
  },
  {
    title: "OneUBank MFB Business",
    description:
      "Business banking app built for secure operations, account visibility, and streamlined financial workflows.",
    url: "https://play.google.com/store/apps/details?id=com.one_u_bank.business",
    featureImage: "",
    gradient: "linear-gradient(135deg, #2563eb 0%, #0f172a 100%)",
    accent: "#dbeafe",
  },
];

const extractFeatureImageFromHtml = (html: string): string | null => {
  const metaPattern =
    /<meta[^>]+(?:property|name)=["']og:image["'][^>]+content=["']([^"']+)["'][^>]*>/i;
  const match = html.match(metaPattern);

  if (match?.[1]) {
    return match[1];
  }

  const imagePattern =
    /https?:\/\/[^\s"']+\.(?:png|jpg|jpeg|webp)(?:\?[^\s"']*)?/i;
  const imageMatch = html.match(imagePattern);

  return imageMatch?.[0] ?? null;
};

const scrapeGooglePlayFeatureImage = async (
  playStoreUrl: string,
): Promise<string | null> => {
  const sources = [
    playStoreUrl,
    `https://r.jina.ai/http://${playStoreUrl.replace(/^https?:\/\//, "")}`,
  ];

  for (const source of sources) {
    try {
      const response = await fetch(source, {
        headers: {
          Accept:
            "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
          "User-Agent":
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0.0.0 Safari/537.36",
        },
      });

      if (!response.ok) {
        continue;
      }

      const html = await response.text();
      const featureImage = extractFeatureImageFromHtml(html);

      if (featureImage) {
        return featureImage;
      }
    } catch (error) {
      console.warn("Failed to fetch Play Store feature image:", error);
    }
  }

  return null;
};

function ProjectsSection() {
  const [projects, setProjects] = useState(initialProjects);

  useEffect(() => {
    let isMounted = true;

    const loadProjectFeatureImages = async () => {
      const updatedProjects = await Promise.all(
        initialProjects.map(async (project) => {
          const featureImage = await scrapeGooglePlayFeatureImage(project.url);

          return {
            ...project,
            featureImage: featureImage ?? project.featureImage,
          };
        }),
      );

      if (isMounted) {
        setProjects(updatedProjects);
      }
    };

    loadProjectFeatureImages();

    return () => {
      isMounted = false;
    };
  }, []);
  return (
    <section
      id="projects"
      className="flex min-h-screen w-full items-center justify-center bg-[#1e1e1e] px-4 py-12 md:px-12"
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-white md:text-4xl">
            Projects
          </h2>
          <img
            src={require("../../asset/image/section-indicator.png")}
            alt="section indicator"
            className="mx-auto mt-3 block"
          />
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {projects.map((project) => (
            <a
              key={project.title}
              href={project.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-lg shadow-black/20 transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div
                className="mb-4 flex h-40 items-center justify-center overflow-hidden rounded-xl text-4xl font-bold"
                style={{
                  backgroundImage: project.featureImage
                    ? `url("${project.featureImage}")`
                    : project.gradient,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  color: project.accent,
                }}
              >
                {!project.featureImage &&
                  (project.title.split(" ")[1] || "App")}
              </div>
              <h3 className="mb-3 text-xl font-semibold text-white">
                {project.title}
              </h3>
              <p className="text-sm leading-7 text-slate-200">
                {project.description}
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ProjectsSection;
